import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Car, Eye, EyeOff, Wrench } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'
import { getErrorMessage, getPublicErrorMessage } from '@/lib/errors'
import { cn, formatarWhatsApp, isWhatsAppValido, normalizarWhatsApp } from '@/lib/utils'
import { toast } from 'sonner'
import { VERTICAIS, type TipoOperacao } from '@/config/verticais'

function gerarSlug(nome: string) {
  return nome
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 50)
}

const TIPOS_DISPONIVEIS: Array<{ tipo: TipoOperacao; titulo: string; descricao: string }> = [
  { tipo: 'loja', titulo: VERTICAIS.loja.rotulo, descricao: VERTICAIS.loja.descricao },
  { tipo: 'oficina', titulo: VERTICAIS.oficina.rotulo, descricao: VERTICAIS.oficina.descricao },
]

export default function Cadastro() {
  const navigate = useNavigate()
  const { reload } = useAuth()
  const [tipoOperacao, setTipoOperacao] = useState<TipoOperacao>('loja')
  const [nomeLoja, setNomeLoja] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [confirmarSenha, setConfirmarSenha] = useState('')
  const [mostrarSenha, setMostrarSenha] = useState(false)
  const [aceitouTermos, setAceitouTermos] = useState(false)
  const [loading, setLoading] = useState(false)

  const nomeEmpresaLabel = tipoOperacao === 'oficina' ? 'Nome da oficina' : 'Nome da loja'
  const nomeEmpresaPlaceholder =
    tipoOperacao === 'oficina' ? 'Ex: Oficina Central' : 'Ex: Auto Center Silva'

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const whatsappNormalizado = normalizarWhatsApp(whatsapp)

    if (!nomeLoja.trim() || !whatsappNormalizado || !email || !senha || !confirmarSenha) {
      toast.error('Preencha todos os campos')
      return
    }
    if (!isWhatsAppValido(whatsappNormalizado)) {
      toast.error('Informe um WhatsApp válido com DDD')
      return
    }
    if (senha.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres')
      return
    }
    if (senha !== confirmarSenha) {
      toast.error('As senhas não conferem')
      return
    }
    if (!aceitouTermos) {
      toast.error('Você precisa aceitar os Termos de Uso e a Política de Privacidade')
      return
    }

    setLoading(true)

    try {
      // 1. Criar usuário no Auth primeiro
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password: senha,
      })

      if (authError) {
        if (authError.status === 429 || authError.message.toLowerCase().includes('rate limit')) {
          throw new Error('Muitas tentativas de cadastro. Aguarde alguns minutos e tente novamente.')
        }
        if (authError.message.toLowerCase().includes('signups are disabled') || authError.message.toLowerCase().includes('signup is disabled')) {
          throw new Error('Cadastro desabilitado. Entre em contato com o administrador.')
        }
        if (authError.message.toLowerCase().includes('already registered')) {
          throw new Error('Este e-mail já possui uma conta. Faça login.')
        }
        throw authError
      }
      if (!authData.user) throw new Error('Falha ao criar usuário')

      // 2. Login automático para obter sessão autenticada
      const { error: loginError } = await supabase.auth.signInWithPassword({
        email,
        password: senha,
      })

      if (loginError) throw loginError

      // 3. Criar loja (agora autenticado)
      const lojaId = crypto.randomUUID()
      const slug = gerarSlug(nomeLoja) + '-' + Date.now().toString(36)
      const { error: lojaError } = await supabase
        .from('lojas')
        .insert({
          id: lojaId,
          nome: nomeLoja.trim(),
          slug,
          whatsapp: whatsappNormalizado,
          tipo_operacao: tipoOperacao,
        })

      if (lojaError) throw lojaError

      // 4. Inserir perfil
      const { error: perfilError } = await supabase
        .from('perfis')
        .insert({
          id: authData.user.id,
          loja_id: lojaId,
          nome: null,
          papel: 'admin',
        })

      if (perfilError) throw perfilError

      await reload()
      toast.success('Conta criada!')
      navigate('/dashboard')
    } catch (err: unknown) {
      console.error('Erro no cadastro:', err)
      toast.error(getPublicErrorMessage(getErrorMessage(err), 'Erro ao criar conta. Tente novamente.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4 py-8">
      <div className="w-full max-w-sm space-y-6">
        {/* Logo */}
        <div className="flex flex-col items-center gap-2">
          <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center">
            <Car className="h-6 w-6 text-primary-foreground" />
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold">WeAuto</h1>
            <p className="text-sm text-muted-foreground">Sistema de Avaliação 360°</p>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-xl">Cadastre sua empresa</CardTitle>
            <CardDescription>Crie sua conta e comece a usar</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Qual o tipo da sua operação?</Label>
                <div
                  role="radiogroup"
                  aria-label="Tipo de operação"
                  className="grid gap-2"
                >
                  {TIPOS_DISPONIVEIS.map(({ tipo, titulo, descricao }) => {
                    const selecionado = tipoOperacao === tipo
                    const Icone = tipo === 'oficina' ? Wrench : Car
                    return (
                      <button
                        key={tipo}
                        type="button"
                        role="radio"
                        aria-checked={selecionado}
                        onClick={() => setTipoOperacao(tipo)}
                        disabled={loading}
                        className={cn(
                          'flex w-full items-start gap-3 rounded-lg border bg-background/70 p-3 text-left transition-colors',
                          'hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          selecionado
                            ? 'border-primary ring-1 ring-primary'
                            : 'border-border'
                        )}
                      >
                        <div
                          className={cn(
                            'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                            selecionado
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground'
                          )}
                        >
                          <Icone className="h-4 w-4" />
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm font-medium leading-none">{titulo}</p>
                          <p className="text-xs text-muted-foreground">{descricao}</p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="nomeLoja">{nomeEmpresaLabel}</Label>
                <Input
                  id="nomeLoja"
                  placeholder={nomeEmpresaPlaceholder}
                  value={nomeLoja}
                  onChange={e => setNomeLoja(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="whatsapp">WhatsApp</Label>
                <Input
                  id="whatsapp"
                  inputMode="tel"
                  placeholder="(11) 99999-9999"
                  value={whatsapp}
                  onChange={e => setWhatsapp(formatarWhatsApp(e.target.value))}
                  autoComplete="tel"
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoComplete="email"
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="senha">Senha</Label>
                <div className="relative">
                  <Input
                    id="senha"
                    type={mostrarSenha ? 'text' : 'password'}
                    placeholder="Mínimo 6 caracteres"
                    value={senha}
                    onChange={e => setSenha(e.target.value)}
                    autoComplete="new-password"
                    disabled={loading}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarSenha(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {mostrarSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmar">Confirmar senha</Label>
                <Input
                  id="confirmar"
                  type={mostrarSenha ? 'text' : 'password'}
                  placeholder="Repita a senha"
                  value={confirmarSenha}
                  onChange={e => setConfirmarSenha(e.target.value)}
                  autoComplete="new-password"
                  disabled={loading}
                />
              </div>

              <div className="rounded-lg border bg-background/70 p-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="aceitou-termos"
                    checked={aceitouTermos}
                    onCheckedChange={(checked) => setAceitouTermos(checked === true)}
                    disabled={loading}
                    className="mt-0.5"
                  />
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <Label htmlFor="aceitou-termos" className="text-sm font-normal leading-5 text-foreground">
                      Li e aceito os{' '}
                      <Link to="/termos-de-uso" className="font-medium text-primary hover:underline">
                        Termos de Uso
                      </Link>{' '}
                      e a{' '}
                      <Link to="/politica-de-privacidade" className="font-medium text-primary hover:underline">
                        Política de Privacidade
                      </Link>
                      .
                    </Label>
                    <p>
                      Ao contratar um plano, a cobrança é recorrente conforme o plano escolhido. Você pode
                      cancelar a qualquer momento.
                    </p>
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Criando conta...' : 'Criar conta'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          Já tem conta?{' '}
          <Link to="/login" className="font-medium text-primary hover:underline">
            Fazer login
          </Link>
        </p>

        <p className="text-center text-xs text-muted-foreground">
          Dúvidas sobre cobrança, cancelamento ou privacidade? Consulte nossos{' '}
          <Link to="/termos-de-uso" className="font-medium text-primary hover:underline">
            Termos
          </Link>{' '}
          e a{' '}
          <Link to="/politica-de-privacidade" className="font-medium text-primary hover:underline">
            Política de Privacidade
          </Link>
          .
        </p>
      </div>
    </div>
  )
}
